import type { Me, RoleKey } from '@myrewards/contracts';
import { canUseSystemDiagnostics } from '../admin/health/access';

/**
 * The Settings areas (PRD §17 "Settings areas by persona", NAV0 Level 2): each person sees only
 * the areas their roles can see, in this order. Everyone has My preferences. System administration is part of Settings and blends with every other role's areas.
 */
export type SettingsAreaId =
  | 'me'
  | 'my-feedback'
  | 'sector'
  | 'executive-delegates'
  | 'budgets'
  | 'values'
  | 'award-rules'
  | 'charge-codes'
  | 'elections-letters'
  | 'help-catalog'
  | 'messages'
  | 'payroll'
  | 'system'
  | 'health'
  | 'debug'
  | 'organization'
  | 'spot-setup'
  | 'preferences'
  | 'people'
  | 'directory'
  | 'roles'
  | 'types'
  | 'notifications'
  | 'ai'
  | 'preview'
  | 'audit'
  | 'sent-mail'
  | 'feedback'
  | 'go-live'
  | 'imports'
  | 'rollover';

export interface SettingsArea {
  id: SettingsAreaId;
  label: string;
  href: string;
  /** The sidebar's group. */
  group: 'My preferences' | 'Award setup' | 'System administration';
}

const AREAS: readonly (SettingsArea & { roles: readonly RoleKey[] | 'everyone' })[] = [
  {
    id: 'me',
    label: 'My preferences',
    href: '/settings/me',
    group: 'My preferences',
    roles: 'everyone',
  },
  {
    // APP-138 §D8: what the person sent with the Feedback button, and the team's replies. Shown
    // when they can send feedback now or have sent some (`showsMyFeedback`).
    id: 'my-feedback',
    label: 'My feedback',
    href: '/settings/me/feedback',
    group: 'My preferences',
    roles: 'everyone',
  },
  {
    id: 'sector',
    label: 'Sector setup',
    href: '/settings/sector',
    group: 'Award setup',
    roles: ['sector_lead', 'executive', 'stock_admin', 'finance', 'system_admin'],
  },
  {
    // APP-274: an Executive keeps their own; the Stock Admin team reads them, as a sector's.
    id: 'executive-delegates',
    label: 'Executive delegates',
    href: '/settings/delegates',
    group: 'Award setup',
    roles: ['executive', 'stock_admin', 'system_admin'],
  },
  {
    id: 'budgets',
    label: 'Budgets',
    href: '/settings/budgets',
    group: 'Award setup',
    roles: ['executive', 'stock_admin', 'finance', 'system_admin'],
  },
  {
    id: 'values',
    label: 'Values',
    href: '/settings/values',
    group: 'Award setup',
    roles: ['executive', 'stock_admin', 'finance', 'payroll', 'system_admin'],
  },
  {
    id: 'award-rules',
    label: 'Award rules',
    href: '/settings/award-rules',
    group: 'Award setup',
    roles: ['stock_admin', 'system_admin'],
  },
  {
    // APP-220: the Stock Admin team and Executives name the Spot Committee too.
    id: 'spot-setup',
    label: 'Spot Award setup',
    href: '/settings/spot',
    group: 'Award setup',
    roles: ['executive', 'stock_admin', 'system_admin'],
  },
  {
    id: 'charge-codes',
    label: 'Charge codes',
    href: '/settings/charge-codes',
    group: 'Award setup',
    roles: ['executive', 'stock_admin', 'finance', 'payroll', 'system_admin'],
  },
  {
    id: 'elections-letters',
    label: 'Elections & letters',
    href: '/settings/elections-letters',
    group: 'Award setup',
    roles: ['executive', 'stock_admin', 'payroll', 'system_admin'],
  },
  {
    // APP-101: was Tax estimate; the company rate is retired.
    id: 'payroll',
    label: 'Payroll',
    href: '/settings/payroll',
    group: 'Award setup',
    roles: ['stock_admin', 'payroll', 'system_admin'],
  },
  {
    id: 'help-catalog',
    label: 'Help catalog',
    href: '/settings/help-catalog',
    group: 'Award setup',
    roles: ['stock_admin', 'system_admin'],
  },
  {
    // APP-137: the wording of every email, bell line and Teams card.
    id: 'messages',
    label: 'Messages',
    href: '/settings/messages',
    group: 'Award setup',
    roles: ['stock_admin', 'system_admin'],
  },
  {
    id: 'system',
    label: 'Overview',
    href: '/settings/system',
    group: 'System administration',
    roles: ['system_admin'],
  },
  {
    id: 'health',
    label: 'System health',
    href: '/settings/system/health',
    group: 'System administration',
    roles: ['system_admin'],
  },
  {
    id: 'debug',
    label: 'System debug',
    href: '/settings/system/debug',
    group: 'System administration',
    roles: ['system_admin'],
  },
  {
    id: 'organization',
    label: 'Organization',
    href: '/settings/system/organization',
    group: 'System administration',
    roles: ['system_admin'],
  },
  {
    id: 'preferences',
    label: 'People’s preferences',
    href: '/settings/system/preferences',
    group: 'System administration',
    roles: ['system_admin'],
  },
  {
    id: 'people',
    label: 'People',
    href: '/settings/system/people',
    group: 'System administration',
    roles: ['system_admin'],
  },
  {
    id: 'directory',
    label: 'Directory sync',
    href: '/settings/system/directory',
    group: 'System administration',
    roles: ['system_admin'],
  },
  {
    id: 'roles',
    label: 'Roles & lists',
    href: '/settings/system/roles',
    group: 'System administration',
    roles: ['system_admin'],
  },
  {
    id: 'types',
    label: 'Award types & features',
    href: '/settings/system/types',
    group: 'System administration',
    roles: ['system_admin'],
  },
  {
    // APP-092 (SYS5's "Edit notification emails"): which emails go, the digest, M5's queue mode.
    id: 'notifications',
    label: 'Notifications',
    href: '/settings/system/notifications',
    group: 'System administration',
    roles: ['system_admin'],
  },
  {
    id: 'ai',
    label: 'AI',
    href: '/settings/system/ai',
    group: 'System administration',
    roles: ['system_admin'],
  },
  {
    id: 'preview',
    label: 'Preview & testing',
    href: '/settings/system/preview',
    group: 'System administration',
    roles: ['system_admin'],
  },
  {
    id: 'audit',
    label: 'Audit log',
    href: '/settings/system/audit-log',
    group: 'System administration',
    roles: ['system_admin'],
  },
  {
    id: 'sent-mail',
    label: 'Sent mail',
    href: '/settings/system/sent-mail',
    group: 'System administration',
    roles: ['system_admin'],
  },
  {
    // APP-104 §6: System Admins and Support (the Support role reaches nothing else here).
    id: 'feedback',
    label: 'Feedback',
    href: '/settings/system/feedback',
    group: 'System administration',
    roles: ['system_admin', 'support'],
  },
  {
    id: 'go-live',
    label: 'Go-live checklist',
    href: '/settings/system/go-live',
    group: 'System administration',
    roles: ['system_admin'],
  },
  {
    id: 'imports',
    label: 'CSV imports',
    href: '/settings/system/imports',
    group: 'System administration',
    roles: ['system_admin'],
  },
  {
    id: 'rollover',
    label: 'Year rollover',
    href: '/settings/system/rollover',
    group: 'System administration',
    roles: ['system_admin'],
  },
];

type AreaReader = Pick<Me, 'roles'> & Partial<Pick<Me, 'preview' | 'actingAs' | 'features'>>;

/**
 * My feedback is there for someone who can send feedback now, or who has sent some: reading
 * their own never depends on the switch (APP-138 §D8, §D14).
 */
function showsMyFeedback(me: AreaReader): boolean {
  const feedback = me.features?.feedback;
  return feedback?.enabled === true || feedback?.mine?.any === true;
}

/** The Settings areas this person may open, in the fixed order. */
export function settingsAreasFor(me: AreaReader): SettingsArea[] {
  const held = new Set(me.roles.map((r) => r.role));
  return AREAS.filter(
    (a) =>
      (a.roles === 'everyone' || a.roles.some((r) => held.has(r))) &&
      (!(a.id === 'health' || a.id === 'debug') || canUseSystemDiagnostics(me)) &&
      (a.id !== 'my-feedback' || showsMyFeedback(me)),
  ).map(({ id, label, href, group }) => ({ id, label, href, group }));
}

export function canOpenArea(me: AreaReader, id: SettingsAreaId): boolean {
  return settingsAreasFor(me).some((a) => a.id === id);
}

/**
 * NAV0's decision: a sidebar for owners with many areas, tabs under the hero for one or two, and
 * no Level 2 bar at all for someone with only My preferences.
 */
export function settingsNavKind(count: number): 'none' | 'tabs' | 'sidebar' {
  if (count < 2) return 'none';
  return count <= 2 ? 'tabs' : 'sidebar';
}

/** Whether this person holds any of these roles (and isn't previewing, which is read-only). */
export function holds(me: Pick<Me, 'roles'>, ...roles: readonly RoleKey[]): boolean {
  return me.roles.some((r) => roles.includes(r.role));
}
